import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { io, type Socket } from "socket.io-client";
import { env } from "@/env";
import { getNativeIdentity, getRealtimeToken } from "@/lib/native-auth";
import { useAuthStore } from "@/store/auth.store";
import { resolveRecordingRoute } from "./recording-route";

export const useSocketIoClient = ({
  roomId,
}: {
  roomId?: string;
} = {}): {
  socket: Socket;
  routeError: boolean;
  /**
   * Dials again after the connection was lost: a fresh token and a fresh
   * recording route (a reservation is single-use), then connect. Resolves
   * false when it could not dial (offline, route lookup failed, account
   * changed); the caller decides when to try again.
   */
  redial: () => Promise<boolean>;
} => {
  const [routeError, setRouteError] = useState(false);
  const redialRef = useRef<() => Promise<boolean>>(async () => false);
  const socket = useMemo(
    () =>
      io(env.VITE_TRANSCRIPTION_SOCKET_URL, {
        path: env.VITE_TRANSCRIPTION_SOCKET_PATH,
        autoConnect: false,
        withCredentials: true,
        // A Manager of its own: its reconnection setting is changed below.
        forceNew: true,
      }),
    [roomId],
  );
  useEffect(() => {
    let active = true;
    let dialed = false;
    const owner = getNativeIdentity()?.uid;
    const controller = new AbortController();
    const retire = () => {
      active = false;
      controller.abort();
      socket.auth = {};
      socket.disconnect();
    };
    const refresh = async () => {
      try {
        if (!owner || getNativeIdentity()?.uid !== owner) {
          retire();
          return;
        }
        const token = await getRealtimeToken();
        if (!active || getNativeIdentity()?.uid !== owner) return;
        if (!dialed) {
          const route = await resolveRecordingRoute({
            roomId,
            signal: controller.signal,
          });
          if (!active || getNativeIdentity()?.uid !== owner) return;
          if (route) socket.io.opts.path = route.path;
          socket.auth = {
            nativeSessionToken: token,
            ...(route ? { reservationId: route.reservationId } : {}),
          };
          dialed = true;
          socket.io.reconnection(false);
          socket.connect();
        }
      } catch {
        if (!active) return;
        // No route for the first dial, or the session could not be verified
        // again (an outage, or offline): fail closed. The socket stays
        // closed without credentials (the 60 s check never dials it); only
        // an explicit redial, with a newly issued token, may connect again.
        setRouteError(true);
        dialed = true;
        socket.auth = {};
        socket.disconnect();
      }
    };
    redialRef.current = async (): Promise<boolean> => {
      if (!active || !dialed || socket.connected) return false;
      try {
        if (!owner || getNativeIdentity()?.uid !== owner) return false;
        const token = await getRealtimeToken();
        if (!active || getNativeIdentity()?.uid !== owner) return false;
        const route = await resolveRecordingRoute({
          roomId,
          signal: controller.signal,
        });
        if (!active || getNativeIdentity()?.uid !== owner) return false;
        if (socket.connected) return false;
        if (route) socket.io.opts.path = route.path;
        socket.auth = {
          nativeSessionToken: token,
          ...(route ? { reservationId: route.reservationId } : {}),
        };
        socket.connect();
        return true;
      } catch {
        return false;
      }
    };
    const unsubscribe = useAuthStore.subscribe((state) => {
      if (state.user?.uid !== owner) retire();
    });
    void refresh();
    const timer = setInterval(() => {
      void refresh();
    }, 60000);
    return () => {
      redialRef.current = async () => false;
      clearInterval(timer);
      unsubscribe();
      retire();
    };
  }, [socket, roomId]);
  const redial = useCallback(() => redialRef.current(), []);
  return { socket, routeError, redial };
};
